// "use client";

import {
  Box,
  Container,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Typography,
  stepConnectorClasses,
} from "@mui/material";
import type { StepIconProps } from "@mui/material/StepIcon";
import { styled } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { JSX } from "react";
import { PAGE_COPY } from "@/constants/pages";

const steps = PAGE_COPY.booking.stepper.steps;
const stepHints = PAGE_COPY.booking.stepper.hints;

const BookingConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 20,
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.divider,
    borderTopWidth: 3,
    borderRadius: 999,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.primary.main,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.primary.main,
  },
}));

const StepIconRoot = styled("div")<{
  ownerState: { active?: boolean; completed?: boolean };
}>(({ ownerState, theme }) => {
  const isActive = Boolean(ownerState.active);
  const isCompleted = Boolean(ownerState.completed);

  return {
    zIndex: 1,
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.common.white,
    transition: "all 0.2s ease",
    backgroundColor: isActive
      ? theme.palette.warning.main
      : isCompleted
        ? theme.palette.primary.main
        : theme.palette.text.disabled,
    boxShadow: isActive ? `0 6px 16px ${alpha(theme.palette.warning.main, 0.35)}` : "none",
  };
});

function StepIcon(props: StepIconProps) {
  const { active, completed, className, icon } = props;

  const icons: Record<string, JSX.Element> = {
    1: <EventAvailableOutlinedIcon sx={{ fontSize: 20 }} />,
    2: <MarkEmailReadOutlinedIcon sx={{ fontSize: 20 }} />,
    3: <CreditCardOutlinedIcon sx={{ fontSize: 20 }} />,
    4: <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 20 }} />,
  };

  return (
    <StepIconRoot ownerState={{ active, completed }} className={className}>
      {icons[String(icon)]}
    </StepIconRoot>
  );
}

type BookingStepperProps = {
  activeStep: 0 | 1 | 2 | 3;
};

export const BookingStepper = ({ activeStep }: BookingStepperProps) => {
  return (
    <Container maxWidth="sm">
      <Box sx={{ mb: 4 }}>
        <Stepper alternativeLabel activeStep={activeStep} connector={<BookingConnector />}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={StepIcon}
                sx={{
                  "& .MuiStepLabel-label": {
                    mt: 1,
                    fontWeight: 600,
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  },
                  "& .MuiStepLabel-label.Mui-active": {
                    color: "warning.main",
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Typography textAlign="center" color="text.secondary" mt={2}>
          {stepHints[activeStep]}
        </Typography>
      </Box>
    </Container>
  );
};
